/**
 * Folder Hierarchy Service
 * 
 * Handles all hierarchical folder operations for the case_documents table.
 * This service provides a clean abstraction over the new hierarchical folder structure.
 * 
 * Key Responsibilities:
 * - Folder CRUD operations (create, read, update, delete)
 * - Tree building and navigation
 * - Path validation and normalization
 * - Category-specific rules enforcement (e.g., partie_adverse)
 * - Folder content queries
 * 
 * NOTE: This service now uses the Backend API (OVH Cloud PostgreSQL) via apiClient
 * instead of direct Supabase calls for improved security and performance.
 * 
 * @module services/folderHierarchyService
 */

import { extractLeadingPieceNumber } from "@hector/piece-number-sort";
import { PARTIE_ADVERSE_PAIR, type PieceNamingConvention } from "@hector/shared-types";
import { apiClient } from "@/lib/apiClient";
import { storageService } from "@/services/storageService";
import type { UploadResult as StorageUploadResult } from "@/services/storageService";
import { getFileType, getMimeType, isSupportedFileType, SUPPORTED_FILE_TYPES } from "@/utils/fileProcessing";
import { convertHeicToJpeg, HeicConversionError, isHeicFile } from "@/utils/heicConversion";
import { classifyError, getUploadFailureMessage, reportError } from "@/lib/errors";
import type { ErrorCategory, ErrorReason, ApiError, ClassifiedError } from "@/lib/errors";
import { SYSTEM_FOLDER_DOCUMENT_TYPE } from "@/lib/partieAdverseFolders";

// ============================================================================
// Types & Interfaces
// ============================================================================

/**
 * Raw `case_documents` row as returned by the Backend API (OVH PostgreSQL).
 *
 * Couvre à la fois les dossiers (`is_folder=true`) et les documents : la table
 * est polymorphe et la couche service discrimine via `is_folder`. Les champs
 * connus sont typés explicitement (consommés par `mapToFolderNode`, le comptage
 * récursif, le déplacement cross-catégorie et les consommateurs externes qui
 * filtrent sur `document_subcategory`/`document_type`). L'index signature
 * `[key: string]: unknown` garde la forme ouverte sans réintroduire `any` :
 * le payload backend peut porter des colonnes supplémentaires non listées ici.
 */
export interface CaseDocumentRecord {
  id?: string;
  name?: string;
  is_folder?: boolean;
  case_id?: string;
  document_category?: string;
  document_subcategory?: string | null;
  document_type?: string;
  folder_path?: string[];
  parent_folder_path?: string[];
  display_folder_path?: string | null;
  folder_depth?: number;
  document_count?: number;
  created_at?: string;
  [key: string]: unknown;
}

export interface FolderNode {
  id: string;
  name: string;
  path: string[];
  depth: number;
  parentPath: string[];
  displayPath: string | null;
  isFolder: boolean;
  children: FolderNode[];
  documentCount?: number; // Total documents recursively
  folderCount?: number; // Immediate subfolders count
  fileCount?: number; // Immediate files count
  caseId: string;
  category: string;
  subcategory?: string;
  createdAt: string;
  /** Marqueur des dossiers système ('system_folder' pour les canoniques partie adverse). */
  documentType?: string;
  /** Rang de tri explicite (migration 124) — null tant qu'aucun reorder/seed ne l'a posé. */
  siblingOrder?: number | null;
}

/**
 * Contenu d'un niveau de dossier. Volontairement réduit à ce que la grille
 * consomme : le chemin courant est déjà l'entrée de l'appel, et le fil d'Ariane
 * se dérive de ce même chemin côté `useFolderNavigation` — le renvoyer ici
 * dupliquait la génération sans consommateur.
 */
export interface FolderContents {
  folders: FolderNode[];
  documents: CaseDocumentRecord[];
}

export interface Breadcrumb {
  name: string;
  path: string[];
  displayPath: string;
}

export interface CreateFolderParams {
  caseId: string;
  category: string;
  parentPath: string[];
  folderName: string;
  subcategory?: string;
  throwIfExists?: boolean;
  /**
   * `document_type` posé sur la ligne dossier. Utilisé par les dossiers
   * système auto-créés (« Écritures/Pièces adverses » → 'system_folder') :
   * identité DB partagée avec la migration 143 pour que le script et le
   * frontend reconnaissent mutuellement les canoniques qu'ils créent.
   */
  documentType?: string;
}

export interface UploadResult {
  successCount: number;
  failureCount: number;
  createdFolders: string[];
  uploadedDocuments: string[];
  errors: string[];
}

export interface BatchUploadResult {
  totalFiles: number;
  successCount: number;
  /**
   * `category` conserve le contrat large historique ; `reasonCode` décrit la
   * cause précise pour le message et la décision de retry.
   */
  failedFiles: Array<{
    file: File;
    error: string;
    category: ErrorCategory;
    reasonCode: ErrorReason;
    statusCode?: number;
    retryable: boolean;
  }>;
  uploadedDocuments: Array<{ documentId: string; fileName: string }>;
  cancelled?: boolean;
}

export interface UploadProgress {
  totalFiles: number;
  uploadedFiles: number;
  currentFile: string;
  percentage: number;
}

export interface CaseMetadata {
  dossierID: string;
  dossierName: string;
  clientName: string;
  lawyerName: string;
  typeDeProcedure: string;
}

// File size limits (in bytes)
// Note: Requires Supabase Pro plan or higher for large uploads
export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB per file
export const MAX_TOTAL_SIZE = 1024 * 1024 * 1024; // 1GB (1024MB) total per batch

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

// ============================================================================
// Folder Hierarchy Service Class
// ============================================================================

export class FolderHierarchyService {
  // Category-specific configuration
  private readonly MAX_FOLDER_DEPTH = 10;
  // Partie adverse now supports full folder hierarchy since mandatory folders are UI-only
  private readonly PARTIE_ADVERSE_MAX_DEPTH = 10;
  
  // Folder cache for upload session (prevents redundant checks)
  private folderCache: Map<string, boolean> = new Map();
  
  // Parallel upload configuration
  // Increased to 15 for better performance with large batches (150+ files)
  // Concurrence d'upload volontairement modérée : 15 POST multipart simultanés
  // saturaient l'upstream de l'utilisateur (surtout en visio concurrente) →
  // resets TCP → "Failed to fetch" en rafale (incident import 2026-06-15 :
  // 60 échecs réseau / 51 succès entrelacés chez la même cliente). 6 limite la
  // pression sur le lien montant tout en gardant un débit correct. Combiné au
  // retry réseau borné par fichier (cf. uploadFileToFolder), les blips se
  // résorbent sans surfacer d'échec à l'utilisateur.
  private readonly MAX_PARALLEL_UPLOADS = 6;
  
  // File d'attente des rejeux d'upload (cf. runInUploadRetryLane).
  private uploadRetryLane: Promise<void> = Promise.resolve();

  // Upload cancellation support
  private uploadAbortController: AbortController | null = null;

  // Cases dont la création de la paire canonique partie adverse est en vol.
  // Garde anti-double-création partagée par TOUS les appelants (StrictMode
  // double-mount, ou PartieAdverseSection + MoveToDialog ouverts en même temps) :
  // `findFolderByPath` dans `createFolder` ne dédoublonne que séquentiellement.
  private canonicalPartieAdverseInFlight: Set<string> = new Set();

  // ============================================================================
  // Public API - Folder Operations
  // ============================================================================

  /**
   * Garantit l'existence de la paire canonique « Écritures adverses » /
   * « Pièces adverses » pour un dossier. Idempotent : matche par marqueur
   * `system_folder` + tag (rename-proof), repli par nom.
   *
   * Filet frontend résiduel (2026-07-21) : le backend garantit désormais cette
   * paire à `POST /api/cases` et M146 backfille le stock. L'ensure reste appelé
   * au mount de PartieAdverseSection pour réparer un rare échec fail-soft du
   * second write backend. MoveToDialog n'appelle plus cette méthode : il consomme
   * l'arbre canonique sans porter de logique de réparation.
   *
   * @returns true si au moins un dossier a été créé (l'appelant invalide alors
   *          ses queries de dossiers).
   */
  async ensureCanonicalPartieAdverseFolders(caseId: string): Promise<boolean> {
    if (!caseId || this.canonicalPartieAdverseInFlight.has(caseId)) return false;
    this.canonicalPartieAdverseInFlight.add(caseId);
    try {
      const contents = await this.getFolderContents(caseId, 'partie_adverse', []);
      let createdSome = false;
      for (const spec of PARTIE_ADVERSE_PAIR) {
        const existing = (contents.folders || []).find(
          (f) =>
            f.path.length === 1 &&
            ((f.documentType === SYSTEM_FOLDER_DOCUMENT_TYPE && f.subcategory === spec.kind) ||
              f.name === spec.name),
        );
        if (existing) continue;
        await this.createFolder({
          caseId,
          category: 'partie_adverse',
          parentPath: [],
          folderName: spec.name,
          subcategory: spec.kind,
          documentType: SYSTEM_FOLDER_DOCUMENT_TYPE,
        });
        createdSome = true;
      }
      return createdSome;
    } finally {
      this.canonicalPartieAdverseInFlight.delete(caseId);
    }
  }

  /**
   * Get the complete folder tree for a case and category
   * Returns a hierarchical structure with nested folders and document counts
   */
  async getFolderTree(caseId: string, category: string, subcategory?: string): Promise<FolderNode[]> {
    try {
      const params = new URLSearchParams({
        category,
        ...(subcategory && { subcategory }),
      });
      
      const data = await apiClient.get<CaseDocumentRecord[]>(
        `/cases/${caseId}/documents/tree?${params}`
      );

      return this.buildTreeFromFlatData(data || []);
    } catch (error) {
      console.error('Error fetching folder tree:', error);
      throw error;
    }
  }

  /**
   * Get contents of a specific folder (direct children only)
   * Returns folders and documents at the specified path
   */
  async getFolderContents(
    caseId: string,
    category: string,
    folderPath: string[],
    subcategory?: string
  ): Promise<FolderContents> {
    try {
      const params = new URLSearchParams({
        category,
        ...(subcategory && { subcategory }),
        folderPath: JSON.stringify(folderPath),
      });
      
      // Backend returns { folders: [], documents: [], breadcrumbs: [], folderCounts: {} }
      interface FolderContentsResponse {
        folders?: CaseDocumentRecord[];
        documents?: CaseDocumentRecord[];
        breadcrumbs?: Breadcrumb[];
        folderCounts?: Record<string, number>;
      }
      const url = `/cases/${caseId}/documents/contents?${params}`;
      const contents = await apiClient.get<FolderContentsResponse>(url);

      const folders = contents?.folders || [];
      const documents = contents?.documents || [];
      const folderCounts = contents?.folderCounts || {};
      
      // Synthesis visibility is now controlled at the UI layer via feature_flags.alpha_synthesis
      // (migration 058 dropped the is_dev column). The service returns all documents; callers
      // such as useDeepSearch apply the canSeeSynthesis filter from useSynthesisPermissions.
      const filteredDocuments = documents;

      // Le compte vient du BACKEND, et de lui seul (2026-09-18).
      //
      // Il passait auparavant par un cache alimenté par l'arbre, préféré quand il
      // était chaud : deux calculs cohabitaient donc, le cache comptant les
      // fichiers en RÉCURSIF et le backend les enfants DIRECTS. Le même dossier
      // affichait l'un ou l'autre selon les écrans visités avant, sans que rien
      // ne l'annonce. Le cache existait pour contourner une requête backend qui
      // ne filtrait pas la catégorie ; cette requête est corrigée, le cache n'a
      // plus de raison d'être, et la carte a désormais UNE définition.
      const enrichedFolders = folders.map((folder) => {
        const folderNode = this.mapToFolderNode(folder);
        const totalCount = folderCounts[folderNode.id] || 0;

        folderNode.folderCount = totalCount;
        folderNode.fileCount = totalCount;
        folderNode.documentCount = totalCount;
        return folderNode;
      });

      return {
        folders: enrichedFolders,
        documents: filteredDocuments
      };
    } catch (error) {
      console.error('[getFolderContents] Error:', error);
      throw error;
    }
  }

  /**
   * Create a new folder
   */
  async createFolder(params: CreateFolderParams): Promise<FolderNode> {
    const { caseId, category, parentPath, folderName, subcategory, throwIfExists = false, documentType } = params;
    
    // Validate folder creation
    const validation = this.validateFolderCreation(category, parentPath, folderName);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }

    const newPath = [...parentPath, folderName];
    const depth = newPath.length;

    // Check if folder already exists (handle duplicates gracefully)
    const existing = await this.findFolderByPath(caseId, category, newPath, subcategory);
    if (existing) {
      if (throwIfExists) {
        throw new Error(`Un dossier nommé "${folderName}" existe déjà à cet emplacement.`);
      }
      // Folder already exists - return it instead of throwing error
      console.log(`[createFolder] Folder already exists: ${newPath.join('/')}, returning existing`);
      return existing;
    }

    try {
      // Create folder entry via API
      const data = await apiClient.post<CaseDocumentRecord>(`/cases/${caseId}/documents/folder`, {
        name: folderName,
        document_category: category,
        document_subcategory: subcategory || null,
        // Marqueur des dossiers système (le backend insère le body en
        // passthrough) — null pour les dossiers utilisateur ordinaires.
        document_type: documentType || null,
        folder_path: newPath,
        parent_folder_path: parentPath,
        display_folder_path: newPath.join('/'),
        folder_depth: depth
      });

      return this.mapToFolderNode(data);
    } catch (error) {
      // Check if error is due to duplicate - try to fetch existing folder
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage.includes('duplicate') || errorMessage.includes('already exists')) {
        if (throwIfExists) {
          throw new Error(`Un dossier nommé "${folderName}" existe déjà à cet emplacement.`);
        }
        console.log(`[createFolder] Duplicate error, fetching existing folder: ${newPath.join('/')}`);
        const existingAfterError = await this.findFolderByPath(caseId, category, newPath, subcategory);
        if (existingAfterError) {
          return existingAfterError;
        }
      }
      console.error('Error creating folder:', error);
      throw error;
    }
  }

  /**
   * Rename a folder (updates all child paths recursively)
   */
  async renameFolder(
    caseId: string,
    category: string,
    folderPath: string[],
    newName: string,
    subcategory?: string
  ): Promise<void> {
    // Validate new name
    if (!newName || !newName.trim()) {
      throw new Error('Folder name cannot be empty');
    }

    // Get the folder
    const folder = await this.findFolderByPath(caseId, category, folderPath, subcategory);
    if (!folder) {
      throw new Error('Folder not found');
    }

    const oldPath = folderPath;
    const newPath = [...folderPath.slice(0, -1), newName.trim()];

    // Check if new path already exists
    const existing = await this.findFolderByPath(caseId, category, newPath, subcategory);
    if (existing) {
      throw new Error(`Un dossier nommé "${newName.trim()}" existe déjà à cet emplacement.`);
    }

    try {
      // Rename folder via API (backend handles children updates)
      await apiClient.patch(`/cases/${caseId}/documents/folders/${folder.id}`, {
        name: newName.trim(),
        category,
        subcategory,
        oldPath,
        newPath
      });
    } catch (error) {
      console.error('Error renaming folder:', error);
      throw error;
    }
  }

  /**
   * Delete a folder
   * @param deleteContents - If true, deletes all contents; if false, moves to parent
   */
  async deleteFolder(
    caseId: string,
    category: string,
    folderPath: string[],
    deleteContents: boolean = false,
    subcategory?: string
  ): Promise<{ filesFailed?: number }> {
    const folder = await this.findFolderByPath(caseId, category, folderPath, subcategory);
    if (!folder) {
      throw new Error('Folder not found');
    }

    try {
      // Delete folder via API (backend handles contents)
      // `filesFailed` > 0 : dossier supprimé, des fichiers sont restés dans le stockage.
      return await apiClient.delete<{ filesFailed?: number }>(
        `/cases/${caseId}/documents/folders/${folder.id}?deleteContents=${deleteContents}&category=${category}${subcategory ? `&subcategory=${subcategory}` : ''}`
      );
    } catch (error) {
      console.error('Error deleting folder:', error);
      throw error;
    }
  }

  /**
   * Move a folder to a new location
   */
  async moveFolder(
    caseId: string,
    category: string,
    sourcePath: string[],
    targetParentPath: string[],
    subcategory?: string
  ): Promise<void> {
    // Validate move
    const validation = this.validateFolderMove(category, sourcePath, targetParentPath);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }

    const folderName = sourcePath[sourcePath.length - 1] ?? '';
    const newPath = [...targetParentPath, folderName];

    // Check if destination already exists
    const existing = await this.findFolderByPath(caseId, category, newPath, subcategory);
    if (existing) {
      throw new Error(`Un dossier nommé "${folderName}" existe déjà dans le dossier de destination.`);
    }

    // Get the source folder to get its subcategory
    const sourceFolder = await this.findFolderByPath(caseId, category, sourcePath, subcategory);
    if (!sourceFolder) {
      throw new Error('Source folder not found');
    }

    try {
      // Move folder via API (backend handles children updates)
      await apiClient.patch(`/cases/${caseId}/documents/folders/${sourceFolder.id}/move`, {
        category,
        subcategory: sourceFolder.subcategory,
        sourcePath,
        targetParentPath,
        newPath
      });
    } catch (error) {
      console.error('Error moving folder:', error);
      throw error;
    }
  }

  // ============================================================================
  // Public API - Document Operations
  // ============================================================================

  /**
   * Ensure all folders in a path exist
   */
  private async ensureFolderPathExists(
    caseId: string,
    category: string,
    folderPath: string[],
    subcategory?: string
  ): Promise<void> {
    console.log(`[ensureFolderPathExists] Called with path:`, folderPath, 'subcategory:', subcategory);
    if (folderPath.length === 0) return;

    // Build path incrementally and create missing folders
    for (let i = 0; i < folderPath.length; i++) {
      const currentPath = folderPath.slice(0, i + 1);
      const parentPath = currentPath.slice(0, -1);
      const folderName = currentPath[currentPath.length - 1] ?? '';

      console.log(`[ensureFolderPathExists] Checking folder at depth ${i + 1}:`, currentPath);

      // Check if folder exists
      const existing = await this.findFolderByPath(caseId, category, currentPath, subcategory);
      
      if (!existing) {
        // Create folder
        console.log(`[ensureFolderPathExists] Creating missing folder: ${currentPath.join('/')} with subcategory:`, subcategory);
        await this.createFolder({
          caseId,
          category,
          parentPath,
          folderName,
          subcategory
        });
        console.log(`[ensureFolderPathExists] Successfully created folder: ${currentPath.join('/')}`);
      } else {
        console.log(`[ensureFolderPathExists] Folder already exists: ${currentPath.join('/')}`);
      }
    }
    console.log(`[ensureFolderPathExists] Completed for path:`, folderPath);
  }

  /**
   * Exécute un rejeu d'upload seul, après les rejeux déjà en attente.
   *
   * Un POST sans réponse est presque toujours un envoi trop lent pour le délai
   * (lien montant faible divisé par `MAX_PARALLEL_UPLOADS`). Rejouer en série rend
   * tout le débit au fichier rejoué — incident 2026-10-07 : 6 fichiers relancés
   * ensemble trois fois de suite, 0 arrivé au backend.
   */
  private async runInUploadRetryLane<T>(task: () => Promise<T>): Promise<T> {
    const previous = this.uploadRetryLane;
    let release: () => void = () => {};
    this.uploadRetryLane = new Promise<void>(resolve => { release = resolve; });
    try {
      await previous;
      // L'attente peut durer plusieurs minutes : ne pas envoyer après une annulation.
      if (this.uploadAbortController?.signal.aborted) {
        throw new DOMException('Upload cancelled while waiting to retry', 'AbortError');
      }
      return await task();
    } finally {
      release();
    }
  }

  /**
   * Upload a file to a specific folder
   *
   * @param pieceNamingConvention Convention du dossier (`resolvePieceNamingConvention`
   *   sur `cases.procedure_type`). `administratif` reconnaît aussi « 1 Carte
   *   d'identité.pdf » comme pièce 1 ; `civil` (défaut) garde le comportement historique.
   */
  async uploadFileToFolder(
    caseId: string,
    category: string,
    folderPath: string[],
    file: File,
    additionalMetadata?: Record<string, unknown>,
    subcategory?: string,
    skipFolderCheck?: boolean,  // Skip folder creation if already done in batch
    pieceNamingConvention: PieceNamingConvention = 'civil'
  ): Promise<{ documentId: string }> {
    // Photo iPhone : convertie en JPEG AVANT tout le reste, pour que le libellé, le
    // `file_path` S3 et le `document_type` décrivent le fichier réellement envoyé, et que la
    // pièce suive ensuite le chemin d'une image JPEG (aperçu, OCR, synthèse, bordereau).
    // Rejette avec `HeicConversionError`, dont `batchUploadFiles` montre le message tel quel.
    const uploadableFile = isHeicFile(file) ? await convertHeicToJpeg(file) : file;

    // Ensure folder path exists (skip if already done in batch upload)
    if (!skipFolderCheck) {
      await this.ensureFolderPathExists(caseId, category, folderPath, subcategory);
    }
    
    // Create sanitized file for storage upload (fixes special character issues)
    // Original file name is preserved in database, but storage uses sanitized version
    const sanitizedFile = this.createSanitizedFile(uploadableFile);
    
    console.log(`[uploadFileToFolder] Original: "${file.name}"`);
    console.log(`[uploadFileToFolder] Sanitized file name: "${sanitizedFile.name}"`);

    // Step 1: Upload to storage via backend API
    //
    // Retry borné UNIQUEMENT quand la requête N'A REÇU AUCUNE RÉPONSE HTTP —
    // c'est le mode de panne exact diagnostiqué en prod (req. jamais arrivée au
    // backend : 0 log Render, blip réseau client). Deux formes seulement :
    //   - `TypeError` "Failed to fetch" (échec transport navigateur)
    //   - `DOMException` TimeoutError/AbortError = notre `AbortSignal.timeout`
    // On NE rejoue JAMAIS dès qu'un `statusCode` existe : un 4xx (fichier rejeté,
    // doublon) est définitif, et rejouer un 5xx sur un batch de 30 fichiers en
    // parallèle = tempête de requêtes sur un backend déjà en difficulté.
    // NB sûreté : `uploadAbortController` n'est PAS câblé dans le fetch (il n'est
    // que sondé entre lots) — la seule source d'Abort/Timeout sur CETTE requête
    // est donc notre timeout, jamais une annulation user. Traiter ces erreurs
    // comme rejouables ici est correct (≠ règle générale "abort ≠ user cancel").
    // `storageService.uploadFile` recrée un AbortSignal.timeout neuf par appel
    // → chaque tentative a son propre timer (pas de signal sticky).
    const MAX_UPLOAD_RETRIES = 2;
    const isNoResponseError = (e: unknown): boolean =>
      e instanceof TypeError ||
      (e instanceof DOMException && (e.name === 'TimeoutError' || e.name === 'AbortError'));
    let uploadResult: StorageUploadResult | undefined;
    for (let attempt = 0; ; attempt++) {
      try {
        const send = () => storageService.uploadFile(sanitizedFile, {
          caseId,
          category,
          folderPath
        });
        // Les rejeux passent UN PAR UN : rejoués ensemble, les fichiers d'un même
        // lot se repartagent le lien montant et retombent sur le même timeout.
        const res = attempt === 0 ? await send() : await this.runInUploadRetryLane(send);
        // uploadFile renvoie un tableau pour un envoi multi-fichiers ; ici un seul.
        const single = Array.isArray(res) ? res[0] : res;
        if (!single) {
          throw new Error(`Storage upload returned an empty result for "${file.name}"`);
        }
        uploadResult = single;

        // ✅ CRITICAL: Use exact filePath returned by S3 upload
        console.log(`[uploadFileToFolder] S3 returned filePath: "${uploadResult.path}"`);
        break;
      } catch (uploadError) {
        const canRetry = isNoResponseError(uploadError) && attempt < MAX_UPLOAD_RETRIES;
        console.error(
          `[uploadFileToFolder] Storage upload error (attempt ${attempt + 1}/${MAX_UPLOAD_RETRIES + 1}, ` +
          `noResponse=${isNoResponseError(uploadError)}, willRetry=${canRetry}):`,
          uploadError
        );
        if (!canRetry) throw uploadError;
        // Annulation user pendant le backoff : ne pas relancer une tentative
        // morte (le signal n'est sondé qu'entre lots, pas dans le fetch).
        if (this.uploadAbortController?.signal.aborted) throw uploadError;
        // Backoff exponentiel : 800 ms puis 2400 ms — laisse le temps à un
        // micro-incident réseau (handoff Wi-Fi, proxy qui reset) de se résorber.
        await new Promise(r => setTimeout(r, 800 * Math.pow(3, attempt)));
      }
    }

    if (!uploadResult) {
      // Inatteignable : la boucle ci-dessus ne sort que par `break` (après
      // affectation) ou par `throw`. Garde pour le narrowing TS strict.
      throw new Error(`Storage upload returned no result for "${file.name}"`);
    }

    // Step 2: Create document record in database via API
    // ✅ CRITICAL: Use uploadResult.path (exact S3 path) not custom path
    const documentId = crypto.randomUUID();

    // Determine document type from file extension (more reliable than file.type)
    const fileType = getFileType(uploadableFile.name);
    const mimeType = getMimeType(fileType) || uploadableFile.type || 'application/octet-stream';

    // Auto-détection du numéro de pièce que l'avocat a déjà écrit dans le nom
    // du fichier, au dépôt — UNIQUEMENT pour les pièces (« Nos pièces » /
    // « Partie adverse »), pas pour la recherche/notes où `piece_number` n'a
    // pas de sens. « PIÈCE 14. Acte d'honoraires » → piece_number '14' +
    // libellé « Acte d'honoraires » (le numéro est retiré du libellé). Si le
    // nom n'est QUE le numéro avec marqueur (« Pièce 14 »), le numéro est
    // extrait et le libellé gardé verbatim. Parseur déterministe (0 LLM),
    // entièrement réversible via les éditeurs de pièce. Cf. plan
    // docs/features/piece-ingestion/plans/2026-07-07-auto-extract-piece-number-on-deposit.html
    //
    // Droit public (#1704) : Télérecours impose un nom de fichier qui commence
    // par le numéro d'ordre (R. 414-5 CJA), d'où « 1 Carte d'identité.pdf ».
    // L'espace n'est admise comme séparateur que pour ces dossiers : en civil,
    // « 3 quittances de loyer » reste une quantité et n'est pas numérotée.
    const isPieceCategory = category === "nos_pieces" || category === "partie_adverse";
    const { pieceNumber, label } = isPieceCategory
      ? extractLeadingPieceNumber(uploadableFile.name, {
          allowSpaceAfterBareNumber: pieceNamingConvention === 'administratif',
        })
      : { pieceNumber: null as string | null, label: uploadableFile.name };

    const insertData = {
      id: documentId,
      name: label,
      ...(pieceNumber ? { piece_number: pieceNumber } : {}),
      file_path: uploadResult.path,  // ✅ EXACT S3 PATH from upload response
      document_type: uploadResult.contentType || mimeType,
      document_category: category,
      document_subcategory: subcategory || null,
      folder_path: folderPath,
      folder_depth: folderPath.length,
      parent_folder_path: folderPath.length > 0 ? folderPath.slice(0, -1) : [],
      display_folder_path: folderPath.length > 0 ? folderPath.join('/') : null,
      is_folder: false,
      file_size: uploadResult.size || uploadableFile.size,  // ✅ Use S3 size
      ...additionalMetadata
    };

    // Création du record DB — UN SEUL essai, volontairement PAS d'auto-retry.
    //
    // Contrairement à l'upload S3 (Step 1, idempotent : ré-uploader recrée
    // l'objet), cet INSERT n'est PAS idempotent : `documentId` est généré
    // client-side et envoyé comme `id`, et le backend fait un `create()` = INSERT
    // simple (`api.routes.ts` POST /cases/:caseId/documents). Rejouer après une
    // panne "sans réponse" où la 1ʳᵉ requête a en fait ABOUTI (réponse perdue)
    // renverrait le même `id` → violation d'unicité → puis notre cleanup S3
    // supprimerait un fichier désormais référencé par le record créé = record
    // orphelin. Le retry de cette étape est couvert SANS risque par le bouton UI
    // (`useDirectDropUpload.retryFailed`), qui régénère un `documentId` neuf.
    try {
      // Create document record via API (backend will validate S3 path exists)
      await apiClient.post<unknown>(`/cases/${caseId}/documents`, insertData);
      return { documentId };
    } catch (dbError) {
      // Nettoyer l'objet S3 orphelin (aucun record DB ne le référence) puis
      // RE-THROW l'erreur d'origine TELLE QUELLE.
      //
      // Ne JAMAIS la ré-emballer dans `new Error("Database error: …")` : ça
      // détruisait `statusCode` / `errorCode` / `requestId` / le type ApiError,
      // ce qui (1) cassait la classification en aval — un vrai 5xx tombait en
      // `unexpected` au lieu de `network` faute de statusCode — et (2) rendait le
      // diagnostic opaque (PostHog ne voyait que "Database error: Failed to
      // fetch"). cf. incident import 2026-06-15.
      console.error('[uploadFileToFolder] Database insert failed:', dbError);
      try {
        await storageService.removeFiles(uploadResult.path);
      } catch (cleanupError) {
        console.error('[uploadFileToFolder] Storage cleanup failed:', cleanupError);
      }
      throw dbError;
    }
  }

  /**
   * Upload a file (simplified interface for compatibility)
   */
  async uploadFile(
    caseId: string,
    category: string,
    folderPath: string[],
    file: File,
    subcategory?: string
  ): Promise<{ success: boolean; documentId?: string; error?: string }> {
    try {
      const result = await this.uploadFileToFolder(caseId, category, folderPath, file, {}, subcategory);
      return { success: true, documentId: result.documentId };
    } catch (error) {
      console.error('Upload error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * 🚀 OPTIMIZED: Batch create all folders needed for upload
   * Creates folders in order of depth, in batches
   * CRITICAL: Ensures ALL parent folders are created, not just leaf folders
   */
  async batchCreateFolders(
    caseId: string,
    category: string,
    folderPaths: string[][],
    subcategory?: string,
    onFoldersCreated?: () => void  // Callback to refresh UI
  ): Promise<void> {
    console.log('[batchCreateFolders] Creating folders:', folderPaths);
    
    // Get unique paths including ALL parent directories
    // Example: ["test", "graphrag"] and ["test", "xxx"] should create:
    // 1. ["test"]  (parent - depth 1)
    // 2. ["test", "graphrag"] (depth 2)
    // 3. ["test", "xxx"] (depth 2)
    const uniquePathSet = new Set<string>();
    
    for (const folderPath of folderPaths) {
      // Add all parent paths
      for (let i = 1; i <= folderPath.length; i++) {
        const parentPath = folderPath.slice(0, i);
        uniquePathSet.add(parentPath.join('/'));
      }
    }
    
    // Convert back to array format and sort by depth
    const uniquePaths = Array.from(uniquePathSet)
      .map(p => p.split('/').filter(s => s))
      .sort((a, b) => a.length - b.length);
    
    if (uniquePaths.length === 0) return;
    
    console.log('[batchCreateFolders] All paths to create (including parents):', uniquePaths.map(p => p.join('/')));
    
    // Clear cache at the start of batch operation
    this.folderCache.clear();
    
    // Check which folders already exist
    const existingFolders = await this.checkExistingFolders(caseId, category, uniquePaths, subcategory);
    
    // Create missing folders in order of depth
    for (const path of uniquePaths) {
      const cacheKey = this.getFolderCacheKey(caseId, category, path, subcategory);
      
      if (this.folderCache.has(cacheKey)) {
        console.log('[batchCreateFolders] Folder in cache:', path.join('/'));
        continue;
      }
      
      if (existingFolders.has(path.join('/'))) {
        console.log('[batchCreateFolders] Folder exists:', path.join('/'));
        this.folderCache.set(cacheKey, true);
        continue;
      }
      
      // Create folder
      try {
        await this.createFolder({
          caseId,
          category,
          parentPath: path.slice(0, -1),
          folderName: path[path.length - 1] ?? '',
          subcategory
        });
        this.folderCache.set(cacheKey, true);
        console.log('[batchCreateFolders] Created folder:', path.join('/'));
      } catch (error) {
        // Ignore "already exists" errors (race condition)
        if (error instanceof Error && !error.message.includes('already exists')) {
          throw error;
        }
        this.folderCache.set(cacheKey, true);
      }
    }
    
    console.log('[batchCreateFolders] Batch creation complete');
    
    // Trigger UI refresh callback
    if (onFoldersCreated) {
      onFoldersCreated();
    }
  }

  /**
   * Check which folders already exist in the database
   */
  private async checkExistingFolders(
    caseId: string,
    _category: string,
    paths: string[][],
    _subcategory?: string
  ): Promise<Set<string>> {
    if (paths.length === 0) return new Set();
    
    try {
      const params = new URLSearchParams({
        paths: JSON.stringify(paths),
      });
      
      const data = await apiClient.get<{ folder_path: string[]; display_folder_path: string }[]>(
        `/cases/${caseId}/documents/folders/check-existing?${params}`
      );
      
      return new Set((data || []).map(d => d.display_folder_path));
    } catch (error) {
      console.error('Error checking existing folders:', error);
      return new Set();
    }
  }

  /**
   * Get cache key for folder
   */
  private getFolderCacheKey(
    caseId: string,
    category: string,
    path: string[],
    subcategory?: string
  ): string {
    return `${caseId}:${category}:${subcategory || 'none'}:${path.join('/')}`;
  }

  /**
   * Validate files before upload
   */
  validateFilesForUpload(files: File[]): ValidationResult {
    const errors: string[] = [];
    let totalSize = 0;
    
    for (const file of files) {
      // Check file extension is supported
      const fileType = getFileType(file.name);
      if (!fileType) {
        errors.push(`${file.name} n'a pas d'extension de fichier valide`);
      } else if (!isSupportedFileType(fileType)) {
        errors.push(
          `${file.name} : Type de fichier non supporté (.${fileType}). Types autorisés : ${SUPPORTED_FILE_TYPES.join(', ')}`
        );
      }
      
      // Check individual file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(
          `${file.name} est trop volumineux (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum: ${MAX_FILE_SIZE / 1024 / 1024}MB`
        );
      }
      
      // Check for empty files
      if (file.size === 0) {
        errors.push(`${file.name} est vide`);
      }
      
      totalSize += file.size;
    }
    
    // Check total batch size
    if (totalSize > MAX_TOTAL_SIZE) {
      errors.push(
        `Taille totale trop grande (${(totalSize / 1024 / 1024).toFixed(1)}MB). Maximum: ${MAX_TOTAL_SIZE / 1024 / 1024}MB`
      );
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Cancel ongoing upload
   */
  cancelUpload(): void {
    if (this.uploadAbortController) {
      // Le contrôleur reste en place jusqu'à la fin du lot : les rejeux en attente
      // lisent `signal.aborted` pour ne pas envoyer après l'annulation.
      this.uploadAbortController.abort();
      console.log('[batchUploadFiles] Upload cancelled by user');
    }
  }

  /**
   * 🚀 OPTIMIZED: Upload multiple files in parallel with progress tracking
   *
   * @param pieceNamingConvention Convention du dossier, résolue par l'appelant qui
   *   a déjà chargé le dossier (`resolvePieceNamingConvention(procedure_type)`).
   *   Voir {@link FolderHierarchyService.uploadFileToFolder}.
   */
  async batchUploadFiles(
    caseId: string,
    category: string,
    files: Array<{ file: File; folderPath: string[] }>,
    subcategory?: string,
    onProgress?: (progress: UploadProgress) => void,
    onFoldersCreated?: () => void,  // Callback to refresh UI immediately after folders created
    pieceNamingConvention: PieceNamingConvention = 'civil'
  ): Promise<BatchUploadResult> {
    const totalFiles = files.length;
    let uploadedFiles = 0;
    const result: BatchUploadResult = {
      totalFiles,
      successCount: 0,
      failedFiles: [],
      uploadedDocuments: [],
      cancelled: false
    };
    
    // Create new AbortController for this upload session
    this.uploadAbortController = new AbortController();
    const signal = this.uploadAbortController.signal;
    
    console.log(`[batchUploadFiles] Starting batch upload of ${totalFiles} files`);
    
    // Validate files first
    const fileList = files.map(f => f.file);
    const validation = this.validateFilesForUpload(fileList);
    if (!validation.isValid) {
      console.error('[batchUploadFiles] Validation failed:', validation.errors);
      // Rejet de validation (taille/total/format) = définitif → non rejouable.
      result.failedFiles = files.map(f => ({
        file: f.file,
        error: validation.errors[0] || 'Validation failed',
        category: 'business',
        reasonCode: 'validation_error',
        retryable: false,
      }));
      return result;
    }
    
    // Check for cancellation
    if (signal.aborted) {
      result.cancelled = true;
      return result;
    }
    
    // Step 1: Collect all unique folder paths
    const allFolderPaths = files.map(f => f.folderPath).filter(p => p.length > 0);
    
    // Step 2: Create all folders in batch (much faster!)
    if (allFolderPaths.length > 0) {
      try {
        await this.batchCreateFolders(caseId, category, allFolderPaths, subcategory, onFoldersCreated);
      } catch (error) {
        console.error('[batchUploadFiles] Folder creation failed:', error);
        // Continue anyway - individual uploads will try to create folders if needed
      }
    }
    
    // Check for cancellation
    if (signal.aborted) {
      result.cancelled = true;
      return result;
    }
    
    // Step 3: Upload files with smooth linear progress
    let filesInProgress = 0;
    let displayedPercentage = 0;
    let progressInterval: NodeJS.Timeout | null = null;
    
    const getActualProgress = () => {
      // Progression réelle basée sur fichiers terminés + estimation pour fichiers en cours
      const estimatedCompleted = uploadedFiles + (filesInProgress * 0.3);
      return Math.min(99, Math.round((estimatedCompleted / totalFiles) * 100));
    };
    
    const updateProgress = () => {
      if (onProgress) {
        onProgress({
          totalFiles,
          uploadedFiles,
          currentFile: '',
          percentage: displayedPercentage
        });
      }
    };
    
    // Timer qui augmente progressivement le % (1% par seconde)
    const startProgressTimer = () => {
      if (progressInterval) return;
      
      progressInterval = setInterval(() => {
        const actualProgress = getActualProgress();
        
        // Augmenter de 1% par seconde, mais ne jamais dépasser la progression réelle
        if (displayedPercentage < actualProgress) {
          displayedPercentage = Math.min(displayedPercentage + 1, actualProgress);
          updateProgress();
        }
      }, 1000);
    };
    
    const stopProgressTimer = () => {
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
    };
    
    const uploadFile = async (fileInfo: { file: File; folderPath: string[] }) => {
      // Check for cancellation before each upload
      if (signal.aborted) {
        return { success: false, error: 'Upload cancelled' };
      }
      
      try {
        // Fichier commence
        filesInProgress++;
        if (filesInProgress === 1) {
          startProgressTimer();
        }
        
        const uploadResult = await this.uploadFileToFolder(
          caseId,
          category,
          fileInfo.folderPath,
          fileInfo.file,
          {},
          subcategory,
          true,  // Skip folder check - already done in batch
          pieceNamingConvention
        );
        
        // Fichier terminé - sauter directement au vrai %
        filesInProgress--;
        uploadedFiles++;
        result.successCount++;
        result.uploadedDocuments.push({
          documentId: uploadResult.documentId,
          fileName: fileInfo.file.name
        });
        
        const actualProgress = getActualProgress();
        displayedPercentage = Math.max(displayedPercentage, actualProgress);
        updateProgress();
        
        return { success: true, documentId: uploadResult.documentId };
      } catch (error) {
        filesInProgress--;
        uploadedFiles++;

        // Observabilité : point de convergence UNIQUE de tous les échecs d'upload.
        // Historiquement l'erreur n'était que `console.error` + re-throw avalé par
        // `Promise.allSettled` → JAMAIS remontée à PostHog (angle mort confirmé :
        // 0 occurrence d'échec upload capturée vs. le chemin chat instrumenté).
        // On capture en direct (pas via ErrorService) car ErrorService skip
        // volontairement PostHog quand `silent && network` (anti-bruit fond) —
        // or un échec d'upload est une action user pleine, pas du bruit de fond.
        // L'erreur rethrow par `uploadFileToFolder` préserve désormais le type
        // ApiError (statusCode inclus). On l'extrait explicitement : sans lui,
        // Le classifier reçoit explicitement le status pour distinguer un 5xx
        // Hector d'un transport sans réponse. cf. incident import 2026-06-15.
        const statusFromError = (error as Partial<ApiError>)?.statusCode;
        const {
          category,
          reason,
          statusCode,
          technicalMessage,
          retryable,
        } = error instanceof HeicConversionError
          ? this.classifyHeicConversionFailure(error)
          : classifyError(error, statusFromError);
        reportError({
          category,
          reason,
          retryable,
          statusCode,
          technicalMessage,
          context: { action: 'upload_document', case_id: caseId },
          originalError: error,
        });

        // Le message dépend de la cause : une connexion interrompue demande
        // une vérification côté utilisateur, tandis qu'un 5xx doit orienter
        // vers le support Hector. Le transport ne doit jamais être présenté
        // comme une indisponibilité globale du service.
        const userMessage = getUploadFailureMessage(
          category,
          technicalMessage,
          statusCode,
          reason
        );
        result.failedFiles.push({
          file: fileInfo.file,
          error: userMessage,
          category,
          reasonCode: reason,
          statusCode,
          retryable,
        });

        const actualProgress = getActualProgress();
        displayedPercentage = Math.max(displayedPercentage, actualProgress);
        updateProgress();

        return { success: false, error: userMessage };
      }
    };
    
    // Upload in parallel batches
    for (let i = 0; i < files.length; i += this.MAX_PARALLEL_UPLOADS) {
      // Check for cancellation between batches
      if (signal.aborted) {
        result.cancelled = true;
        break;
      }
      
      const batch = files.slice(i, i + this.MAX_PARALLEL_UPLOADS);
      await Promise.allSettled(batch.map(uploadFile));
    }
    
    // Stop timer and set final 100%
    stopProgressTimer();
    displayedPercentage = 100;
    if (onProgress) {
      onProgress({
        totalFiles,
        uploadedFiles: result.successCount,
        currentFile: '',
        percentage: 100
      });
    }
    
    // Clear cache and abort controller after batch operation
    this.folderCache.clear();
    this.uploadAbortController = null;
    
    console.log(`[batchUploadFiles] Batch upload complete: ${result.successCount}/${totalFiles} successful`);
    
    return result;
  }

  /**
   * Clear folder cache (call when needed, e.g., after upload session)
   */
  clearFolderCache(): void {
    this.folderCache.clear();
  }

  /**
   * Move a document to a different folder (same category)
   */
  async moveDocument(
    documentId: string,
    newFolderPath: string[]
  ): Promise<void> {
    try {
      await apiClient.patch(`/cases/documents/${documentId}/move`, {
        parentPath: newFolderPath,
        folderPath: newFolderPath,
        displayFolderPath: newFolderPath.length > 0 ? newFolderPath.join('/') : null
      });
    } catch (error) {
      console.error('Error moving document:', error);
      throw error;
    }
  }

  /**
   * Move a document to a different category (and optionally a different folder).
   * Uses the generic document update endpoint to change category, subcategory, and path.
   */
  async moveDocumentToCategory(
    documentId: string,
    targetCategory: string,
    targetSubcategory: string | null,
    targetFolderPath: string[]
  ): Promise<void> {
    try {
      await apiClient.patch(`/documents/${documentId}`, {
        document_category: targetCategory,
        document_subcategory: targetSubcategory,
        folder_path: targetFolderPath,
        parent_folder_path: targetFolderPath.length > 0 ? targetFolderPath.slice(0, -1) : [],
        display_folder_path: targetFolderPath.length > 0 ? targetFolderPath.join('/') : null,
        folder_depth: targetFolderPath.length,
      });
    } catch (error) {
      console.error('Error moving document to category:', error);
      throw error;
    }
  }

  /**
   * Batch move documents to a different category + folder path.
   */
  async batchMoveDocumentsToCategory(
    documentIds: string[],
    targetCategory: string,
    targetSubcategory: string | null,
    targetFolderPath: string[],
    onProgress?: (completed: number, total: number) => void
  ): Promise<{ successCount: number; failedCount: number; errors: string[] }> {
    const totalFiles = documentIds.length;
    let completedFiles = 0;
    let successCount = 0;
    const errors: string[] = [];
    const MAX_PARALLEL_MOVES = 10;

    const moveOne = async (documentId: string) => {
      try {
        await this.moveDocumentToCategory(documentId, targetCategory, targetSubcategory, targetFolderPath);
        completedFiles++;
        successCount++;
        onProgress?.(completedFiles, totalFiles);
        return { success: true };
      } catch (error) {
        completedFiles++;
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        errors.push(`Document ${documentId}: ${errorMsg}`);
        onProgress?.(completedFiles, totalFiles);
        return { success: false, error: errorMsg };
      }
    };

    for (let i = 0; i < documentIds.length; i += MAX_PARALLEL_MOVES) {
      const batch = documentIds.slice(i, i + MAX_PARALLEL_MOVES);
      await Promise.allSettled(batch.map(moveOne));
    }

    return { successCount, failedCount: documentIds.length - successCount, errors };
  }

  /**
   * Move an entire folder (and all its contents) to a different category.
   * Fetches all items under the folder, recomputes paths, and updates each one.
   */
  async moveFolderToCategory(
    caseId: string,
    sourceCategory: string,
    sourceSubcategory: string | undefined,
    sourceFolderPath: string[],
    targetCategory: string,
    targetSubcategory: string | null,
    targetFolderPath: string[],
  ): Promise<{ successCount: number; failedCount: number; errors: string[] }> {
    const errors: string[] = [];
    let successCount = 0;
    let failedCount = 0;
    const MAX_PARALLEL = 10;

    const params = new URLSearchParams({ category: sourceCategory });
    if (sourceSubcategory) params.append('subcategory', sourceSubcategory);

    const allItems = await apiClient.get<CaseDocumentRecord[]>(`/cases/${caseId}/documents?${params}`);

    const itemsToMove = (allItems || []).filter(item => {
      const itemPath: string[] = Array.isArray(item.folder_path) ? item.folder_path : [];
      if (itemPath.length < sourceFolderPath.length) return false;
      return sourceFolderPath.every((seg: string, i: number) => itemPath[i] === seg);
    });

    if (itemsToMove.length === 0) {
      return { successCount: 0, failedCount: 0, errors: [] };
    }

    const parentPrefix = sourceFolderPath.slice(0, -1);

    const moveItem = async (item: CaseDocumentRecord) => {
      try {
        const oldPath: string[] = Array.isArray(item.folder_path) ? item.folder_path : [];
        const relativePath = oldPath.slice(parentPrefix.length);
        const newPath = [...targetFolderPath, ...relativePath];

        await apiClient.patch(`/documents/${item.id}`, {
          document_category: targetCategory,
          document_subcategory: targetSubcategory,
          folder_path: newPath,
          parent_folder_path: newPath.length > 0 ? newPath.slice(0, -1) : [],
          display_folder_path: newPath.length > 0 ? newPath.join('/') : null,
          folder_depth: newPath.length,
        });
        successCount++;
      } catch (error) {
        failedCount++;
        const msg = error instanceof Error ? error.message : 'Unknown error';
        errors.push(`Item ${item.id}: ${msg}`);
      }
    };

    for (let i = 0; i < itemsToMove.length; i += MAX_PARALLEL) {
      const batch = itemsToMove.slice(i, i + MAX_PARALLEL);
      await Promise.allSettled(batch.map(moveItem));
    }

    return { successCount, failedCount, errors };
  }

  /**
   * 🚀 OPTIMIZED: Batch move multiple documents in parallel
   */
  async batchMoveDocuments(
    documentIds: string[],
    newFolderPath: string[],
    onProgress?: (completed: number, total: number) => void
  ): Promise<{ successCount: number; failedCount: number; errors: string[] }> {
    const totalFiles = documentIds.length;
    let completedFiles = 0;
    let successCount = 0;
    const errors: string[] = [];
    const MAX_PARALLEL_MOVES = 10;

    console.log(`[batchMoveDocuments] Moving ${totalFiles} documents to ${newFolderPath.join('/')}`);

    const moveDocument = async (documentId: string) => {
      try {
        await this.moveDocument(documentId, newFolderPath);
        completedFiles++;
        successCount++;

        if (onProgress) {
          onProgress(completedFiles, totalFiles);
        }

        return { success: true };
      } catch (error) {
        completedFiles++;
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        errors.push(`Document ${documentId}: ${errorMsg}`);

        if (onProgress) {
          onProgress(completedFiles, totalFiles);
        }

        return { success: false, error: errorMsg };
      }
    };

    // Process in parallel batches
    for (let i = 0; i < documentIds.length; i += MAX_PARALLEL_MOVES) {
      const batch = documentIds.slice(i, i + MAX_PARALLEL_MOVES);
      await Promise.allSettled(batch.map(moveDocument));
    }

    console.log(`[batchMoveDocuments] Complete: ${successCount}/${totalFiles} successful`);

    return {
      successCount,
      failedCount: documentIds.length - successCount,
      errors
    };
  }

  // ============================================================================
  // Validation Methods
  // ============================================================================

  /**
   * Validate folder creation
   */
  private validateFolderCreation(
    category: string,
    parentPath: string[],
    folderName: string
  ): ValidationResult {
    const errors: string[] = [];

    // Validate folder name
    if (!folderName || !folderName.trim()) {
      errors.push('Folder name cannot be empty');
    }

    if (folderName.includes('/')) {
      errors.push('Folder name cannot contain "/"');
    }

    // Validate depth
    const newDepth = parentPath.length + 1;
    
    if (category === 'partie_adverse') {
      if (newDepth > this.PARTIE_ADVERSE_MAX_DEPTH) {
        errors.push(`Partie adverse can only have ${this.PARTIE_ADVERSE_MAX_DEPTH} level of folders`);
      }
    } else {
      if (newDepth > this.MAX_FOLDER_DEPTH) {
        errors.push(`Maximum folder depth is ${this.MAX_FOLDER_DEPTH}`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate folder move operation
   */
  private validateFolderMove(
    category: string,
    sourcePath: string[],
    targetParentPath: string[]
  ): ValidationResult {
    const errors: string[] = [];

    // Cannot move folder into itself or its children
    // Check using path arrays for exact matching, not string startsWith
    
    // 1. Check if target is the source itself (same path)
    if (sourcePath.length === targetParentPath.length &&
        sourcePath.every((segment, idx) => segment === targetParentPath[idx])) {
      errors.push('Cannot move folder into itself or its subfolders');
    }
    
    // 2. Check if target is inside the source (target is a descendant)
    // Target must be longer and have source as prefix
    if (targetParentPath.length > sourcePath.length) {
      const isDescendant = sourcePath.every((segment, idx) => 
        targetParentPath[idx] === segment
      );
      if (isDescendant) {
        errors.push('Cannot move folder into itself or its subfolders');
      }
    }

    // Validate depth after move
    const newDepth = targetParentPath.length + 1;
    
    if (category === 'partie_adverse' && newDepth > this.PARTIE_ADVERSE_MAX_DEPTH) {
      errors.push(`Partie adverse folders can only be at depth ${this.PARTIE_ADVERSE_MAX_DEPTH}`);
    } else if (newDepth > this.MAX_FOLDER_DEPTH) {
      errors.push(`Maximum folder depth is ${this.MAX_FOLDER_DEPTH}`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Sanitize file name for storage (remove special characters that cause issues with Supabase)
   * Keeps original name for database, but creates safe version for storage
   */
  private sanitizeFileName(fileName: string): string {
    // Normalize unicode characters (é -> e, etc.)
    const normalized = fileName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    
    // Replace any remaining special characters with safe equivalents
    const sanitized = normalized
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[ýÿ]/g, 'y')
      .replace(/[ñ]/g, 'n')
      .replace(/[ç]/g, 'c')
      .replace(/[æ]/g, 'ae')
      .replace(/[œ]/g, 'oe')
      // Remove any other non-ASCII characters and special chars except dot, dash, underscore
      .replace(/[^a-zA-Z0-9._-]/g, '_');
    
    return sanitized;
  }

  /**
   * Un échec de conversion HEIC est un refus du fichier, pas une panne. `classifyError` le
   * rangerait en `unexpected`, et `getUploadFailureMessage` le remplacerait par le message
   * générique, alors que le sien nomme la photo et le geste à faire. `validation_error` est
   * la seule raison dont le message d'origine est montré tel quel à l'avocat ; non
   * rejouable, puisque la même photo échouerait de la même façon.
   */
  private classifyHeicConversionFailure(error: HeicConversionError): ClassifiedError {
    return {
      category: 'business',
      reason: 'validation_error',
      technicalMessage: error.message,
      retryable: false,
    };
  }

  /**
   * Create a sanitized File object for storage upload
   * Original file name is preserved in the database, but storage uses sanitized name
   */
  private createSanitizedFile(file: File): File {
    const sanitizedName = this.sanitizeFileName(file.name);
    
    // Create new File object with sanitized name
    // This ensures Supabase Storage doesn't reject files with special characters
    return new File([file], sanitizedName, {
      type: file.type,
      lastModified: file.lastModified
    });
  }

  /**
   * Find a folder by its path
   */
  private async findFolderByPath(
    caseId: string,
    category: string,
    path: string[],
    subcategory?: string
  ): Promise<FolderNode | null> {
    try {
      const params = new URLSearchParams({
        category,
        path: JSON.stringify(path),
        ...(subcategory && { subcategory }),
      });
      
      // L'endpoint renvoie 200 + data=null si le dossier n'existe pas (lookup optionnel),
      // pas un 404 — on s'en sert pour pré-checker l'existence avant create/move/upload.
      const data = await apiClient.get<CaseDocumentRecord | null>(
        `/cases/${caseId}/documents/folders/by-path?${params}`
      );

      if (!data || !data.id) {
        return null;
      }

      return this.mapToFolderNode(data);
    } catch (error) {
      console.error('Error finding folder:', error);
      return null;
    }
  }

  /**
   * Build hierarchical tree from flat data
   */
  private buildTreeFromFlatData(data: CaseDocumentRecord[]): FolderNode[] {
    const nodeMap = new Map<string, FolderNode>();
    const rootNodes: FolderNode[] = [];
    const allNodes: FolderNode[] = [];

    // Create all nodes (only folders)
    data.forEach(item => {
      if (item.is_folder) {
        const node = this.mapToFolderNode(item);
        node.children = [];
        allNodes.push(node);
        
        // UNIQUE KEY: category + subcategory + displayPath to avoid cross-category collisions
        const subcatKey = item.document_subcategory || '';
        const key = `${item.document_category}:${subcatKey}:${item.display_folder_path || item.name}`;
        nodeMap.set(key, node);
      }
    });

    // Build parent-child relationships
    allNodes.forEach(node => {
      if (node.depth === 1 && (!node.parentPath || node.parentPath.length === 0)) {
        rootNodes.push(node);
      } else if (node.depth > 1) {
        const parentDisplayPath = node.parentPath.join('/');
        const subcatKey = node.subcategory || '';
        const parentKey = `${node.category}:${subcatKey}:${parentDisplayPath}`;
        const parent = nodeMap.get(parentKey);
        
        if (parent) {
          parent.children.push(node);
        } else {
          // Orphan - add to root
          rootNodes.push(node);
        }
      }
    });

    // Count documents in each folder
    rootNodes.forEach(node => this.countDocumentsInTree(node, data));

    return rootNodes;
  }

  /**
   * Pose sur chaque nœud de l'arbre le compte de ses ENFANTS DIRECTS : les
   * fichiers rangés à son niveau exact, plus ses sous-dossiers, un sous-dossier
   * valant 1.
   *
   * MÊME définition que le compte du backend (`getFolderContentsWithCounts`),
   * et c'est volontaire : l'arbre alimente le sélecteur de destination pendant
   * que le backend alimente la grille, et un dossier qui affiche 3 ici et 47 là
   * ne veut plus rien dire. Le compte était RÉCURSIF jusqu'au 2026-09-18 — il
   * annonçait 47 sur un dossier qui, ouvert, montre trois sous-dossiers.
   *
   * La descente reste récursive : elle sert à visiter les enfants, plus à
   * cumuler leurs totaux.
   */
  private countDocumentsInTree(node: FolderNode, allData: CaseDocumentRecord[]): number {
    const nodeFolderPath = node.path || [];
    const nodePathStr = nodeFolderPath.join('/');

    // Count documents at this exact level, strictly filtering by category and subcategory
    // This prevents documents from other categories with same path from being counted
    const fichiersDirects = allData.filter(item => {
      // Must not be a folder
      if (item.is_folder) return false;
      
      // Must be in the same category and subcategory
      if (item.document_category !== node.category) return false;
      
      const itemSubcat = item.document_subcategory || null;
      const nodeSubcat = node.subcategory || null;
      if (itemSubcat !== nodeSubcat) return false;
      
      // Path must match exactly
      const itemPath = Array.isArray(item.folder_path) 
        ? item.folder_path 
        : (item.display_folder_path ? item.display_folder_path.split('/') : []);
      
      const itemPathStr = itemPath.join('/');
      return itemPathStr === nodePathStr;
    }).length;

    // On descend pour poser leur propre compte sur les enfants ; leurs totaux ne
    // remontent PAS ici — un sous-dossier compte pour 1, pas pour ce qu'il porte.
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        this.countDocumentsInTree(child, allData);
      });
    }

    const count = fichiersDirects + (node.children?.length ?? 0);
    node.documentCount = count;

    return count;
  }

  /**
   * Map database record to FolderNode
   */
  private mapToFolderNode(data: CaseDocumentRecord): FolderNode {
    // Les champs requis de `FolderNode` (`id`/`name`/`caseId`/`category`/
    // `createdAt`) sont défaultés à `''` : le backend les renvoie toujours
    // peuplés pour un dossier valide, mais `CaseDocumentRecord` les déclare
    // optionnels (forme ouverte de la table polymorphe). Le fallback préserve
    // le comportement runtime historique (valeur backend) tout en satisfaisant
    // le typage strict — il ne se déclenche que sur un enregistrement dégénéré.
    return {
      id: data.id ?? '',
      name: data.name ?? '',
      path: data.folder_path || [],
      depth: data.folder_depth || 0,
      parentPath: data.parent_folder_path || [],
      displayPath: data.display_folder_path || null,
      isFolder: data.is_folder || false,
      children: [],
      documentCount: data.document_count || 0,
      caseId: data.case_id ?? '',
      category: data.document_category ?? '',
      subcategory: data.document_subcategory || undefined,
      createdAt: data.created_at ?? '',
      documentType: data.document_type || undefined,
      siblingOrder: typeof data.sibling_order === 'number' ? data.sibling_order : null,
    };
  }
}

// Export singleton instance
export const folderHierarchyService = new FolderHierarchyService();
